import {
  ItemPreparationStatuses,
  OrderInternalStatuses,
  OrderPreparationStatuses,
  OrderStatuses,
} from './enums';
import { checkIfScheduledOrder, getOrderInternalStatus } from './utils';
import type { TabBarKey } from 'store/App/constants';
import type { Order, OrderItem } from './types';

export type OrdersGroupedByTab = {
  notBeingPrepared: Order[];
  beingPrepared: Order[];
  scheduled: Order[];
  prepared: Order[];
};

export type TabBarOrderCountMap = {
  new: [number];
  cooking: [number];
  scheduled: [number];
  prepared: [number];
  newAndCooking: [number, number];
};

type SelectItemsForOrder = (orderUUID: Order['uuid']) => OrderItem[];

const createEmptyGroups = (): OrdersGroupedByTab => ({
  notBeingPrepared: [],
  beingPrepared: [],
  scheduled: [],
  prepared: [],
});

export function groupOrdersByInternalStatus(
  orders: Order[],
  selectItems: SelectItemsForOrder,
): OrdersGroupedByTab {
  const grouped = createEmptyGroups();

  orders.forEach((order) => {
    const orderStatus = getOrderInternalStatus(order, selectItems(order.uuid));

    switch (orderStatus) {
      case OrderInternalStatuses.CANCELLED:
      case OrderInternalStatuses.PREPARED:
        grouped.prepared.push(order);
        break;
      case OrderInternalStatuses.BEING_PREPARED:
        grouped.beingPrepared.push(order);
        break;
      case OrderInternalStatuses.SCHEDULED:
        grouped.scheduled.push(order);
        break;
      case OrderInternalStatuses.NOT_BEING_PREPARED:
        grouped.notBeingPrepared.push(order);
        break;
    }
  });

  return grouped;
}

const addItemToOrderGroup = (
  group: Map<Order['uuid'], Order>,
  order: Order,
  item: OrderItem,
) => {
  const groupedOrder = group.get(order.uuid);

  group.set(order.uuid, {
    ...(groupedOrder ?? order),
    item_uuids: [...(groupedOrder?.item_uuids ?? []), item.uuid],
  });
};

export function groupOrderItemsByStatus(
  orders: Order[],
  selectItems: SelectItemsForOrder,
): OrdersGroupedByTab {
  const grouped = {
    notBeingPrepared: new Map<Order['uuid'], Order>(),
    beingPrepared: new Map<Order['uuid'], Order>(),
    scheduled: new Map<Order['uuid'], Order>(),
    prepared: new Map<Order['uuid'], Order>(),
  };

  orders.forEach((order) => {
    const isScheduledFutureOrder = checkIfScheduledOrder(order);

    selectItems(order.uuid).forEach((item) => {
      if (
        order.order_status === OrderStatuses.CANCELLED ||
        order.status === OrderPreparationStatuses.CANCELLED ||
        order.status === OrderPreparationStatuses.COMPLETED ||
        order.status === OrderPreparationStatuses.PREPARED
      ) {
        addItemToOrderGroup(grouped.prepared, order, item);
        return;
      }

      if (order.status === OrderPreparationStatuses.BEING_PREPARED) {
        if (item.status === ItemPreparationStatuses.BEING_PREPARED) {
          addItemToOrderGroup(grouped.beingPrepared, order, item);
        } else if (item.status === ItemPreparationStatuses.NOT_BEING_PREPARED) {
          addItemToOrderGroup(grouped.notBeingPrepared, order, item);
        } else if (
          item.status === ItemPreparationStatuses.PREPARED ||
          item.status === ItemPreparationStatuses.COMPLETED
        ) {
          addItemToOrderGroup(grouped.prepared, order, item);
        }

        return;
      }

      if (isScheduledFutureOrder) {
        addItemToOrderGroup(grouped.scheduled, order, item);
        return;
      }

      addItemToOrderGroup(grouped.notBeingPrepared, order, item);
    });
  });

  return {
    notBeingPrepared: Array.from(grouped.notBeingPrepared.values()),
    beingPrepared: Array.from(grouped.beingPrepared.values()),
    scheduled: Array.from(grouped.scheduled.values()),
    prepared: Array.from(grouped.prepared.values()),
  };
}

export function countGroupedOrdersForTab(
  grouped: OrdersGroupedByTab,
  tab: TabBarKey,
): number {
  switch (tab) {
    case 'new':
      return grouped.notBeingPrepared.length;
    case 'cooking':
      return grouped.beingPrepared.length;
    case 'scheduled':
      return grouped.scheduled.length;
    case 'prepared':
      return grouped.prepared.length;
    case 'newAndCooking':
      return grouped.notBeingPrepared.length + grouped.beingPrepared.length;
    default:
      return grouped.notBeingPrepared.length;
  }
}

export function createTabBarOrderCountMap(
  grouped: OrdersGroupedByTab,
): TabBarOrderCountMap {
  const newCount = grouped.notBeingPrepared.length;
  const cookingCount = grouped.beingPrepared.length;

  return {
    new: [newCount],
    cooking: [cookingCount],
    scheduled: [grouped.scheduled.length],
    prepared: [grouped.prepared.length],
    newAndCooking: [newCount, cookingCount],
  };
}
